import * as yup from 'yup';
import { MiscAssetDTO } from '../../tables/MiscAssets/MiscAssets.meta';
import { EarmarkDTO, OwnerModelDTO, OwnerModelFormDTO, OwnerTypeDTO } from '../../OtherAssets.meta';
import {
  EXCLUDE_OPT,
  getCustomBucketIndex,
} from '../components/AssetBucketSelect/AssetBucketSelect.meta';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';

export interface MiscAssetsFormValues {
  name: string;
  owner: string | null;
  marketValue: number | null;
  saleYear: string | null;
  saleNetEarnings: number | null;
  assetEarmarks: { value: string; label: string }[];
  assetGrowth: number | null;
  customBucketIndex: number | null;
  isLiquid: boolean;
  includeInGrossEstate: boolean;
  orionManagementStyleId: number | null;
  ownerModel: OwnerModelFormDTO[];
}

export interface MiscAssetsFormDTO {
  Name: string;
  Owner: string;
  MarketValue: number | null;
  SaleYear: string | null;
  IsSoldAtRetirement: boolean;
  SaleNetEarnings: number | null;
  AssetEarmark: EarmarkDTO[];
  AssetGrowth: number | null;
  CustomBucketIndex: number | null;
  IsExcludedFromAssetBucketing: boolean;
  IsLiquid: boolean;
  IncludeInGrossEstate: boolean;
  OrionManagementStyleId: number | null;
  OwnerModel: OwnerModelDTO[];
}

export interface UpsertMiscAssetsDTO extends MiscAssetsFormDTO {
  clientId: string;
  Id: string | null;
}

interface DataToForm {
  data: MiscAssetDTO | null;
  options: OtherAssetsDTO['Options'];
}

export const dataToForm = ({ data, options }: DataToForm): MiscAssetsFormValues =>
  data
    ? {
        name: data.Name,
        owner: data.Owner,
        marketValue: data.MarketValue,
        saleYear:
          data.SaleYear !== null
            ? data.SaleYear.toString()
            : data.IsSoldAtRetirement
            ? 'At Retirement'
            : null,
        saleNetEarnings: data.SaleNetEarnings,
        assetEarmarks: data.AssetEarmark.map(({ Type, Tooltip }) => ({
          value: Type,
          label: Tooltip,
        })),
        assetGrowth: data.AssetGrowth,
        customBucketIndex: getCustomBucketIndex({
          orionManagementStyleId: data.OrionManagementStyleId,
          customBucketIndex: data.CustomBucketIndex,
          managementStyles: options.ManagementStyles,
          isExcludedFromAssetBucketing: data.IsExcludedFromAssetBucketing,
          assetBucketingByManagementStyle: options.AssetBucketingByManagementStyle,
        }),
        isLiquid: data.IsLiquid,
        includeInGrossEstate: data.IncludeInGrossEstate,
        orionManagementStyleId: data.OrionManagementStyleId,

        ownerModel: data.OwnerModel.map(owner => ({
          ...owner,
          Percentage: (owner.Percentage * 100).toFixed(2),
        })),
      }
    : {
        name: '',
        owner: '',
        marketValue: null,
        saleYear: null,
        saleNetEarnings: null,
        assetEarmarks: [],
        assetGrowth: null,
        customBucketIndex: null,
        isLiquid: options.MiscellaneousAssetOptions.IsLiquid,
        includeInGrossEstate: true,
        orionManagementStyleId: null,
        ownerModel: [
          {
            Id: null,
            Name: options.MiscellaneousAssetOptions.ownerOptions.People[0].Name,
            OwnerType: OwnerTypeDTO.Principal,
            Percentage: '100',
          },
        ],
      };

export const formToBody = (values: MiscAssetsFormValues): MiscAssetsFormDTO => ({
  Name: values.name,
  Owner: values.owner || '',
  MarketValue: values.marketValue,
  SaleYear: values.saleYear === 'At Retirement' ? null : values.saleYear,
  IsSoldAtRetirement: values.saleYear === 'At Retirement',
  SaleNetEarnings: values.saleNetEarnings,
  AssetEarmark: values.assetEarmarks.map(({ value, label }) => ({ Type: value, Tooltip: label })),
  AssetGrowth: values.assetGrowth ?? 0,
  CustomBucketIndex:
    values.customBucketIndex === EXCLUDE_OPT.value ? null : values.customBucketIndex,
  IsExcludedFromAssetBucketing: values.customBucketIndex === EXCLUDE_OPT.value,
  IsLiquid: values.isLiquid,
  IncludeInGrossEstate: values.includeInGrossEstate,
  OrionManagementStyleId: values.orionManagementStyleId,
  OwnerModel: values.ownerModel
    .filter(b => b.Name && b.Percentage)
    .map(owner => ({
      ...owner,
      Id: owner.Id ?? null,
      Name: owner.Name ?? '',
      OwnerType: owner.OwnerType ?? 0,
      Percentage: Number((parseFloat(owner.Percentage ?? '0') / 100).toFixed(4)),
    })),
});

export const FORM_SCHEMA = yup.object().shape({
  name: yup.string().required('Please provide a name for this asset'),
  marketValue: yup
    .number()
    .nullable()
    .notOneOf([0], 'Please specify a valid market value')
    .required('Please provide a market value'),
  ownerModel: yup
    .array()
    .test(
      'check-total',
      'The total ownership percentage must add up to 100%',
      (ownerModel, ctx) => {
        if (!ownerModel || !Array.isArray(ownerModel)) return false;
        if (ownerModel[0].Name === '') {
          return ctx.createError({
            path: 'ownerModel[0].Id',
            message: 'Please specify an account owner',
          });
        }
        const sum = ownerModel.reduce((acc, obj) => {
          if (obj.Name && obj.Percentage) {
            return acc + (parseFloat(obj.Percentage) || 0);
          }
          return acc;
        }, 0);
        if (sum !== 100) {
          return ctx.createError({
            path: 'ownerModel[0].Id',
            message: 'The total ownership percentage must add up to 100%',
          });
        }
        return sum === 100;
      },
    ),
});
